import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { tenantApiFetch } from './tenantApi';
import { useTenantAuth } from '../context/TenantAuthContext';

const PRIORITIES = ['low', 'normal', 'high', 'urgent'];
const STATUSES = ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'];

export default function TenantSupportTicketFormPage() {
  const { ticketId } = useParams();
  const isEdit = Boolean(ticketId);
  const navigate = useNavigate();
  const { hasPermission, ready, user } = useTenantAuth();

  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('normal');
  const [status, setStatus] = useState('open');
  const [description, setDescription] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ready && isEdit && !hasPermission('tenant.support_tickets.manage')) {
      navigate('/tenant/support', { replace: true });
    }
  }, [ready, isEdit, hasPermission, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isEdit || !hasPermission('tenant.support_tickets.manage')) return;
      try {
        const res = await tenantApiFetch('/api/tenant/users?pageSize=500&status=active&sort=email&order=asc');
        const json = await res.json().catch(() => ({}));
        if (res.ok && !cancelled) setUsers(json.users || []);
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, hasPermission]);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/support-tickets/${ticketId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load ticket');
      const t = json.ticket;
      setSubject(t.subject || '');
      setCategory(t.category || '');
      setPriority(t.priority || 'normal');
      setStatus(t.status || 'open');
      setDescription(t.description || '');
      setResolutionNotes(t.resolution_notes || '');
      setAssignedTo(t.assigned_to_user_id != null ? String(t.assigned_to_user_id) : '');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [isEdit, ticketId]);

  useEffect(() => {
    load();
  }, [load]);

  const submitCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await tenantApiFetch('/api/tenant/support-tickets', {
        method: 'POST',
        body: {
          subject: subject.trim(),
          category: category.trim() || null,
          priority,
          description: description.trim()
        }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Create failed');
      navigate(`/tenant/support/${json.ticket.id}`, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/support-tickets/${ticketId}`, {
        method: 'PATCH',
        body: {
          subject: subject.trim(),
          category: category.trim() || null,
          priority,
          status,
          description,
          resolution_notes: resolutionNotes,
          assigned_to_user_id: assignedTo === '' ? null : Number(assignedTo)
        }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Update failed');
      navigate(`/tenant/support/${ticketId}`, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!ready || !user) return null;

  if (isEdit && loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit ticket' : 'New support ticket'}
      </Typography>
      <Button component={Link} to="/tenant/support" sx={{ mb: 2 }}>
        Back to list
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, maxWidth: 720 }}>
        <Box component="form" onSubmit={isEdit ? submitEdit : submitCreate}>
          <TextField
            fullWidth
            required
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            margin="normal"
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            fullWidth
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            margin="normal"
            inputProps={{ maxLength: 64 }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Priority</InputLabel>
            <Select label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
              {PRIORITIES.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isEdit && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s.replace(/_/g, ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {!isEdit ? (
            <TextField
              fullWidth
              required
              multiline
              minRows={5}
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              margin="normal"
            />
          ) : (
            <>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Resolution notes"
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                margin="normal"
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Assigned to</InputLabel>
                <Select
                  label="Assigned to"
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.id} value={String(u.id)}>
                      {u.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save' : 'Create ticket'}
            </Button>
            <Button component={Link} to={isEdit ? `/tenant/support/${ticketId}` : '/tenant/support'}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
